import { SearchCriteriaType } from "./SearchCriteriaI";

/** JSON envelope for sys_default preference {@code sso_mappings} (matches backend {@link SsoMappings}). */
export type SsoMappingsDoc = {
    sso_mappings: {
        sso_mapping: SsoMappingRule[];
    };
};

export type SsoMappingRule = {
    sso_mapping_type: string;
    sso_mapping_name?: string;
    sso_groups?: SsoMappingItem[];
    sso_organizations?: SsoMappingItem[];
    sso_role?: SsoMappingItem;
};

export type SsoMappingItem = {
    itemId?: number;
    itemName: string;
};

/** UI row: persisted fields plus display label from picker. */
export type SsoMappingItemForm = {
    itemId?: number;
    itemName: string;
    displayName?: string;
};

export type SsoMappingRuleForm = {
    sso_mapping_type: string;
    sso_mapping_name: string;
    sso_groups: SsoMappingItemForm[];
    sso_organizations: SsoMappingItemForm[];
    sso_role: SsoMappingItemForm | null;
};

export function ssoMappingRuleLabel(form: SsoMappingRuleForm, ruleIndex: number): string {
    const name = form.sso_mapping_name?.trim();
    return name ? name : `Rule ${ruleIndex + 1}`;
}

export const STUDENT_ENGAGEMENT_SSO_MAPPING_TYPE = "SE";

export type SsoMappingUiCopy = {
    sectionTitle: string;
    intro: string;
    organizationCriteriaType: SearchCriteriaType["type"];
    emptyRulesMessage: string;
};

export function ssoMappingUiCopyForType(mappingType: string): SsoMappingUiCopy {
    if (normalizeSsoMappingType(mappingType) === STUDENT_ENGAGEMENT_SSO_MAPPING_TYPE) {
        return {
            sectionTitle: "SSO mappings (Connect25)",
            intro: "Map Shibboleth attribute values to security groups, organizations, and roles. Within one rule, every listed group, organization, and role must match. For alternative criteria, add another rule.",
            organizationCriteriaType: "studentEngagementOrganizations",
            emptyRulesMessage: "No mapping rules configured.",
        };
    }

    return {
        sectionTitle: "SSO mappings",
        intro: "Map Shibboleth attribute values to security groups, organizations, and roles. Within one rule, every listed group, organization, and role must match. For alternative criteria, add another rule.",
        organizationCriteriaType: "organizations",
        emptyRulesMessage: "No mapping rules configured.",
    };
}

export function normalizeSsoMappingType(mappingType: string): string {
    return mappingType.trim().toUpperCase();
}

export function emptySsoMappingRuleForm(mappingType: string): SsoMappingRuleForm {
    return {
        sso_mapping_type: normalizeSsoMappingType(mappingType),
        sso_mapping_name: "",
        sso_groups: [],
        sso_organizations: [],
        sso_role: null,
    };
}

export function parseSsoMappingsPref(preferenceValue: string | undefined | unknown): SsoMappingRule[] {
    if (preferenceValue == null || preferenceValue === "") {
        return [];
    }

    let parsed: unknown;
    if (typeof preferenceValue === "string") {
        try {
            parsed = JSON.parse(preferenceValue);
        } catch {
            return [];
        }
    } else if (typeof preferenceValue === "object") {
        parsed = preferenceValue;
    } else {
        return [];
    }

    const mappingList = (parsed as SsoMappingsDoc)?.sso_mappings?.sso_mapping;
    if (!Array.isArray(mappingList)) {
        return [];
    }

    return mappingList.filter((mapping): mapping is SsoMappingRule => mapping != null);
}

export function partitionSsoMappingRulesByType(
    rules: SsoMappingRule[],
    mappingType: string,
): { typedRules: SsoMappingRule[]; otherRules: SsoMappingRule[] } {
    const normalizedType = normalizeSsoMappingType(mappingType);
    const typedRules: SsoMappingRule[] = [];
    const otherRules: SsoMappingRule[] = [];
    for (const rule of rules) {
        if (rule?.sso_mapping_type && normalizeSsoMappingType(rule.sso_mapping_type) === normalizedType) {
            typedRules.push(rule);
        } else if (rule) {
            otherRules.push(rule);
        }
    }
    return { typedRules, otherRules };
}

export function ssoMappingRulesToForm(rules: SsoMappingRule[], mappingType: string): SsoMappingRuleForm[] {
    const normalizedType = normalizeSsoMappingType(mappingType);
    return partitionSsoMappingRulesByType(rules, normalizedType).typedRules.map((rule) => ({
        sso_mapping_type: normalizedType,
        sso_mapping_name: rule.sso_mapping_name ?? "",
        sso_groups: (rule.sso_groups ?? []).map(toItemForm),
        sso_organizations: (rule.sso_organizations ?? []).map(toItemForm),
        sso_role: rule.sso_role ? toItemForm(rule.sso_role) : null,
    }));
}

export function mergeTypedAndOtherSsoMappingRules(
    typedForms: SsoMappingRuleForm[],
    otherRules: SsoMappingRule[],
): SsoMappingRule[] {
    return [...otherRules, ...ssoMappingFormToRules(typedForms)];
}

function toItemForm(item: SsoMappingItem): SsoMappingItemForm {
    return {
        itemId: item.itemId,
        itemName: item.itemName ?? "",
        displayName: item.itemName,
    };
}

export function ssoMappingFormToRules(forms: SsoMappingRuleForm[]): SsoMappingRule[] {
    return forms.map((form) => {
        const rule: SsoMappingRule = {
            sso_mapping_type: normalizeSsoMappingType(form.sso_mapping_type),
        };
        const ssoMappingName = form.sso_mapping_name?.trim();
        if (ssoMappingName) {
            rule.sso_mapping_name = ssoMappingName;
        }
        const groups = cleanItems(form.sso_groups);
        const organizations = cleanItems(form.sso_organizations);
        if (groups.length) {
            rule.sso_groups = groups;
        }
        if (organizations.length) {
            rule.sso_organizations = organizations;
        }
        const role = cleanRole(form.sso_role);
        if (role) {
            rule.sso_role = role;
        }
        return rule;
    });
}

function cleanItems(items: SsoMappingItemForm[]): SsoMappingItem[] {
    return items
        .filter((item) => item.itemName?.trim())
        .map((item) => ({
            itemName: item.itemName.trim(),
            ...(item.itemId != null ? { itemId: item.itemId } : {}),
        }));
}

function cleanRole(role: SsoMappingItemForm | null): SsoMappingItem | undefined {
    if (!role?.itemName?.trim()) {
        return undefined;
    }
    return {
        itemName: role.itemName.trim(),
        ...(role.itemId != null ? { itemId: role.itemId } : {}),
    };
}

export function serializeSsoMappingsPref(rules: SsoMappingRule[]): string {
    const document: SsoMappingsDoc = {
        sso_mappings: {
            sso_mapping: rules,
        },
    };
    return JSON.stringify(document);
}

export function validateSsoMappingForms(forms: SsoMappingRuleForm[]): string | null {
    for (let ruleIndex = 0; ruleIndex < forms.length; ruleIndex++) {
        const form = forms[ruleIndex];
        const ruleLabel = ssoMappingRuleLabel(form, ruleIndex);

        const hasAnyCriterion =
            form.sso_groups.length > 0 || form.sso_organizations.length > 0 || form.sso_role != null;
        if (!hasAnyCriterion) {
            return `${ruleLabel}: add at least one group, organization, or role.`;
        }

        const itemError = validateItemList(`${ruleLabel} groups`, form.sso_groups);
        if (itemError) {
            return itemError;
        }
        const orgError = validateItemList(`${ruleLabel} organizations`, form.sso_organizations);
        if (orgError) {
            return orgError;
        }
        if (form.sso_role) {
            if (!form.sso_role.itemName?.trim()) {
                return `${ruleLabel} role: SSO attribute value is required.`;
            }
            if (form.sso_role.itemId == null) {
                return `${ruleLabel} role: select a 25Live role (ID is required for mapping to apply).`;
            }
        }
    }
    return null;
}

function validateItemList(sectionLabel: string, items: SsoMappingItemForm[]): string | null {
    for (const item of items) {
        if (!item.itemName?.trim()) {
            return `${sectionLabel}: SSO attribute value is required for each row.`;
        }
        if (item.itemId == null) {
            return `${sectionLabel}: each row must have a selected item (ID is required for mapping to apply).`;
        }
    }
    return null;
}
